import { useState, useEffect } from "react";
import { DollarSign, ChevronLeft, ChevronRight } from "lucide-react";
import { getMiningIncomeHistoryAPI } from "../../api/miningApi";

export default function MiningIncome() {
  const [incomeData, setIncomeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [incomeType, setIncomeType] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [userId, setUserId] = useState("");

  useEffect(() => {
    fetchIncomeData(currentPage, incomeType);
  }, [currentPage, incomeType, pageSize, userId]);

  const fetchIncomeData = async (page, type) => {
    try {
      setLoading(true);
      setError(null);
      const response = await getMiningIncomeHistoryAPI(page, pageSize, type, userId);
      if (response.status) {
        setIncomeData(response.data.data);
        setTotalRecords(response.data.total_rows);
        setTotalPages(Math.ceil(response.data.total_rows / pageSize));
      } else {
        setError("Failed to fetch income data");
      }
    } catch (err) {
      setError("Error fetching income data");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleIncomeTypeChange = (value) => {
    setIncomeType(value === "all" ? "" : value);
    setCurrentPage(1);
  };

  const getStatusBadge = (isReleased) => (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
      isReleased
        ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
        : "bg-yellow-500/10 text-yellow-400 border border-yellow-500/20"
    }`}>
      {isReleased ? "Released" : "Pending"}
    </span>
  );

  const getWalletTypeBadge = (walletType) => {
    let cls = "bg-white/[0.05] text-muted-foreground border border-border/50";
    if (walletType === "points") cls = "bg-blue-500/10 text-blue-400 border border-blue-500/20";
    if (walletType === "energy") cls = "bg-orange-500/10 text-orange-400 border border-orange-500/20";
    return (
      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
        {walletType}
      </span>
    );
  };

  const renderPagination = () => {
    return Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
      if (
        page === 1 ||
        page === totalPages ||
        (page >= currentPage - 1 && page <= currentPage + 1)
      ) {
        return (
          <button
            key={page}
            onClick={() => setCurrentPage(page)}
            className={`h-8 w-8 rounded text-xs font-medium transition-colors ${
              page === currentPage
                ? "bg-ginox-green/20 border border-ginox-green/60 text-ginox-green"
                : "border border-border/70 bg-white/[0.02] text-muted-foreground hover:bg-white/[0.05] hover:text-foreground"
            }`}
          >
            {page}
          </button>
        );
      }
      if (page === currentPage - 2 || page === currentPage + 2) {
        return (
          <span key={page} className="h-8 w-8 flex items-center justify-center text-muted-foreground text-xs">
            ...
          </span>
        );
      }
      return null;
    });
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-400 text-base mb-4">{error}</p>
          <button
            onClick={() => fetchIncomeData(currentPage, incomeType)}
            className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
          <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
          Mining
        </div>
        <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground">Income <span className="text-gradient-ginox">History</span></h1>
        <p className="text-sm text-muted-foreground">Track revenue streams and financial performance</p>
      </div>

      {/* Filters */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              Income Type
            </label>
            <select
              value={incomeType === "" ? "all" : incomeType}
              onChange={(e) => handleIncomeTypeChange(e.target.value)}
              className="w-48 rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
            >
              <option value="all">All</option>
              <option value="daily_reward">Daily Reward</option>
              <option value="free_refill">Free Refill</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              User ID
            </label>
            <input
              type="text"
              placeholder="Enter User ID"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              className="w-36 rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
            />
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              Page
            </label>
            <select
              value={currentPage.toString()}
              onChange={(e) => setCurrentPage(parseInt(e.target.value))}
              className="w-16 rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
            >
              {Array.from({ length: Math.min(totalPages, 50) }, (_, i) => {
                let pageNum;
                if (totalPages <= 50) {
                  pageNum = i + 1;
                } else {
                  const start = Math.max(1, currentPage - 25);
                  pageNum = start + i;
                  if (pageNum > totalPages) return null;
                }
                return <option key={pageNum} value={pageNum.toString()}>{pageNum}</option>;
              }).filter(Boolean)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              Page Size
            </label>
            <select
              value={pageSize.toString()}
              onChange={(e) => { setPageSize(parseInt(e.target.value)); setCurrentPage(1); }}
              className="w-20 rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
            >
              <option value="10">10</option>
              <option value="15">15</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr>
              {["User ID", "Date", "Status", "Wallet Type", "Amount", "Type"].map((h) => (
                <th key={h} className="border-b border-border/70 px-4 py-3 text-left text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="py-12 text-center text-sm text-muted-foreground">Loading...</td>
              </tr>
            ) : incomeData.length > 0 ? (
              incomeData.map((record) => (
                <tr key={record._id} className="group hover:bg-white/[0.02] transition-colors">
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{record.userId}</td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">
                    {new Date(record.createdAt).toLocaleDateString("en-US", {
                      year: "numeric", month: "short", day: "numeric",
                      hour: "2-digit", minute: "2-digit",
                    })}
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm">
                    {getStatusBadge(record.is_released)}
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm">
                    {getWalletTypeBadge(record.wallet_type)}
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-emerald-400 font-mono font-semibold">
                    {record.amount}
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-muted-foreground">
                    {record.type.replace("_", " ")}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="py-12 text-center text-sm text-muted-foreground">No income records found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {(totalPages > 1 || loading) && (
        <div className="flex flex-col items-center gap-3 md:flex-row md:items-center md:justify-between">
          <p className="text-xs text-muted-foreground">
            {loading
              ? "Loading..."
              : `Showing ${(currentPage - 1) * pageSize + 1} to ${Math.min(currentPage * pageSize, totalRecords)} of ${totalRecords} records`}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1 || loading}
              className="h-8 w-8 rounded border border-border/70 bg-white/[0.02] text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            {!loading && renderPagination()}
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages || loading}
              className="h-8 w-8 rounded border border-border/70 bg-white/[0.02] text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
